import { openResourceInViewer } from '@setvi/shared/providers/resource-opener';
import type { OpenResourceProps } from '@setvi/shared/providers/resource-opener';

import { useResourceViewActivity } from 'componentsV2/activity';
import { PreviewZoomDialog } from 'componentsV2/pdf-preview-dialog';
import { PrintOrderCartButton } from 'componentsV2/print-order';

import { useResourcePreview } from '../../hooks/use-resource-preview';
import { PreviewUnavailable } from '../preview-unavailable';

interface ResourcePreviewDialogProps extends OpenResourceProps {
  onClose: () => void;
}

/**
 * In-app preview of a single resource, reusing the print-order zoom dialog
 * with an Add to Cart / Remove from Cart action.
 */
export const ResourcePreviewDialog = ({
  onClose,
  ...props
}: ResourcePreviewDialogProps) => {
  const { name, resource, resourceId, page } = props;
  const { url, cartResource, showCartAction, loading } =
    useResourcePreview(props);

  // Times the whole time the dialog is open, reported once when it closes.
  // Analytics keys the row by the `ResourceID` string, not the numeric id.
  useResourceViewActivity(resourceId ?? resource?.ResourceID);

  if (!url)
    return (
      <PreviewUnavailable
        title={name}
        loading={loading}
        onOpenInViewer={() => {
          openResourceInViewer(props);
          onClose();
        }}
        onClose={onClose}
      />
    );

  return (
    <PreviewZoomDialog
      title={name}
      pages={[{ label: name, pdfUrl: url }]}
      initialPage={page}
      // A resource that needs a QR, cover or location dialog hands over to it:
      // this preview closes (ending its view activity) and that dialog opens.
      actions={
        showCartAction && (
          <PrintOrderCartButton resource={cartResource} onHandOff={onClose} />
        )
      }
      onClose={onClose}
    />
  );
};
